import { sortBy } from "lodash";
import { ReactNode, useMemo, useRef, useState } from "react";
import { ComplianceProgramStep } from "types/graphql-schema";

import { stepNames } from "@apps/programs/components/compliance-program-timeline-vocabulary";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Loading from "@components/loading/loading";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

import { ProgramForm } from "../types";
import useProgramFormLoadPreview from "../use-program-form-load-preview";
import {
  GroupStep,
  LoadPreview,
  WriterGroup,
  defaultSecondsPerQuestion,
  defaultToolSavingsPercent,
  formatCount,
  formatHours,
  formatMinutes,
  formatPersonMinutes,
  formatTimePair,
  parseMinutesPerQuestion,
  parsePercent,
  plural,
  stepSavedFraction,
  writerGroups,
  writerSteps,
  writerTime,
} from "./readiness-figures";
import ReadinessIntegrations from "./readiness-integrations";
import { useReadinessTools } from "./readiness-tools";

/**
 * A guess the admin can change, written into the sentence it is part of. Text
 * rather than a number field, so it has no up and down arrows; the keypad on a
 * phone still offers numbers.
 *
 * With `restoreOnBlur`, while the box holds something that is not a value, such
 * as nothing or 0, the figures keep the last one that was, and leaving the box
 * puts that one back in it. Without it, an empty box is passed on as null.
 */
const AssumptionBox = ({
  value,
  parse,
  format,
  onChange,
  ariaLabel,
  restoreOnBlur = false,
  suffix,
}: {
  value: number | null;
  parse: (text: string) => number | null;
  format: (value: number) => string;
  onChange: (value: number | null) => void;
  ariaLabel: string;
  restoreOnBlur?: boolean;
  /** A unit shown after the number, such as "m". */
  suffix?: string;
}) => {
  const [text, setText] = useState(value === null ? "" : format(value));
  // Set on focus and cleared by the mouse-up that follows it: the browser would
  // otherwise turn that click into a caret and drop the selection.
  const justFocused = useRef(false);
  // Reads as a word in the sentence, not a form field: no border, a gray
  // highlight to say it can change, as wide as what is in it, with the unit
  // inside it.
  return (
    <span className="inline-flex items-baseline rounded bg-gray-100 px-1.5 font-medium text-gray-900 hover:bg-gray-200 focus-within:bg-blue-50 focus-within:ring-1 focus-within:ring-blue-300">
      <input
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        className="border-0 bg-transparent p-0 text-sm font-medium text-gray-900 tabular-nums shadow-none outline-none focus:ring-0"
        // As wide as what is typed, so the highlight hugs the number.
        style={{ width: `${Math.max(text.length, 1)}ch` }}
        value={text}
        // The whole value is selected on the way in, so typing replaces it.
        onFocus={(event) => {
          event.currentTarget.select();
          justFocused.current = true;
        }}
        onMouseUp={(event) => {
          if (justFocused.current) {
            event.preventDefault();
            justFocused.current = false;
          }
        }}
        onChange={(event) => {
          setText(event.target.value);
          const parsed = parse(event.target.value);
          if (parsed !== null || !restoreOnBlur) {
            onChange(parsed);
          }
        }}
        onBlur={
          restoreOnBlur && value !== null
            ? () => setText(format(value))
            : undefined
        }
      />
      {suffix}
    </span>
  );
};

const groupNames: Record<WriterGroup["key"], string> = {
  managers: "Managers",
  employees: "Employees",
};

/** Everybody who writes, as one average person: their time, and the saving. */
type AveragePerson = { today: number; saved: number };

/**
 * One figure per person rather than one per group or one in total: "2 hours
 * each" is what an admin can weigh, and "70 hours" across the company means
 * nothing until it is divided up again. The table splits it by group.
 */
const averagePerson = (groups: WriterGroup[]): AveragePerson => {
  const people = groups.reduce((sum, group) => sum + group.writers.length, 0);
  const average = (value: (group: WriterGroup) => number) =>
    people === 0
      ? 0
      : groups.reduce((sum, group) => sum + value(group), 0) / people;
  return {
    today: average((group) => group.todaySeconds),
    saved: average((group) => group.savedSeconds),
  };
};

/** A figure in a headline, in amber. */
const Figure = ({ children }: { children: ReactNode }) => (
  <span className="text-amber-600 tabular-nums">{children}</span>
);

/** An average count, to one decimal where it is not whole: "3", "5.8". */
const averageText = (value: number) =>
  value.toLocaleString("en-US", { maximumFractionDigits: 1 });

/**
 * Who writes how much: a line per group with its total per person, and under it
 * a line per step it writes in, for the people who write it, with how many
 * reviews they write and how many questions each has. With tools left to
 * connect, a last column says what each could be. Times per person only, so no
 * column claims to add up across groups.
 */
const TimeTable = ({
  groups,
  canSave,
}: {
  groups: WriterGroup[];
  canSave: boolean;
}) => {
  const label = useLabel();
  const names = stepNames(label);
  const row = ({
    key,
    name,
    detail,
    people,
    divideBy,
    today,
    saved,
    kind,
  }: {
    key: string;
    name: string;
    /** What the time is made of, in small gray type under the name. */
    detail?: string;
    /** Shown in the People column; a step line leaves it to its group. */
    people?: number;
    /** How many people the times are shared between. */
    divideBy: number;
    today: number;
    saved: number;
    kind: "group" | "step" | "everyone";
  }) => {
    const pair = formatTimePair(today / divideBy, (today - saved) / divideBy);
    return (
      <tr
        key={key}
        className={
          kind === "step"
            ? "text-gray-600"
            : kind === "everyone"
              ? "border-t border-gray-300 font-semibold"
              : "border-t font-medium"
        }
      >
        <td className={kind === "step" ? "py-1.5 pl-4" : "py-2.5"}>
          <div>{name}</div>
          {detail && (
            <div className="text-xs font-normal text-gray-500">{detail}</div>
          )}
        </td>
        <td className="py-1.5 text-right">
          {people !== undefined && formatCount(people)}
        </td>
        <td className="py-1.5 text-right">{pair.today}</td>
        {canSave && (
          <td
            className={`py-1.5 text-right ${kind === "step" ? "text-blue-700" : "text-blue-800"}`}
          >
            {pair.after}
          </td>
        )}
      </tr>
    );
  };
  const sum = (value: (group: WriterGroup) => number) =>
    groups.reduce((total, group) => total + value(group), 0);
  // "5 direct reports × 6 questions": how many reviews each person who writes in
  // the step writes, on average, and how long each one is. Averaged over the
  // same people as the line's time, so the two multiply out to it.
  const reviewNouns: Partial<
    Record<ComplianceProgramStep, [one: string, many: string]>
  > = {
    [ComplianceProgramStep.SelfReview]: [
      label("review"),
      label("review", { pluralize: true }),
    ],
    [ComplianceProgramStep.DownwardReview]: ["direct report", "direct reports"],
    [ComplianceProgramStep.PeerReview]: ["peer", "peers"],
    [ComplianceProgramStep.UpwardReview]: ["manager", "managers"],
  };
  const stepDetail = (line: GroupStep) => {
    // Singular by the number as shown: 1.04 reads "1 direct report".
    const reviews = averageText(line.reviews / line.writers);
    const [one, many] = reviewNouns[line.step] ?? [
      label("review"),
      label("review", { pluralize: true }),
    ];
    return `${reviews} ${reviews === "1" ? one : many} × ${plural(
      line.questionsEach,
      "question",
    )}`;
  };
  const groupDetail = (group: WriterGroup) => {
    const questions = averageText(
      group.writers.reduce((sum, writer) => sum + writer.questions, 0) /
        group.writers.length,
    );
    return `${questions} ${questions === "1" ? "question" : "questions"} each`;
  };

  return (
    // Straight on the card, its first and last columns in line with the text
    // above it.
    <div>
      <table className="w-full text-sm tabular-nums">
        <thead>
          <tr className="text-xs text-gray-500">
            <th className="pb-2 text-left font-medium">Who</th>
            <th className="pb-2 text-right font-medium">People</th>
            <th className="pb-2 text-right font-medium">Time each</th>
            {canSave && (
              <th className="pb-2 text-right font-medium">
                With tools connected
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {groups.flatMap((group) => [
            row({
              key: group.key,
              name: groupNames[group.key],
              detail: groupDetail(group),
              people: group.writers.length,
              divideBy: group.writers.length,
              today: group.todaySeconds,
              saved: group.savedSeconds,
              kind: "group",
            }),
            // Each step for the people in the group who write it: someone
            // who is not reviewed writes no self review, and an average over
            // them would read "0.8 reviews".
            ...group.byStep.map((line) =>
              row({
                key: `${group.key}-${line.step}`,
                name: names[line.step],
                detail: stepDetail(line),
                people: line.writers,
                divideBy: line.writers,
                today: line.todaySeconds,
                saved: line.savedSeconds,
                kind: "step",
              }),
            ),
          ])}
          {/* An average, not a total, so it never has to add up to the rows. */}
          {groups.length > 1 &&
            row({
              key: "everyone",
              name: "Everyone who writes, on average",
              people: sum((group) => group.writers.length),
              divideBy: sum((group) => group.writers.length),
              today: sum((group) => group.todaySeconds),
              saved: sum((group) => group.savedSeconds),
              kind: "everyone",
            })}
        </tbody>
      </table>
    </div>
  );
};

/** One person's time, with what it is under it or, with `inline`, beside it. */
const Figures = ({
  text,
  caption,
  highlighted = false,
  inline = false,
}: {
  text: string;
  caption: string;
  highlighted?: boolean;
  inline?: boolean;
}) => (
  <div className={inline ? "flex items-baseline gap-2" : "flex flex-col"}>
    <span
      className={`text-2xl font-semibold tabular-nums ${highlighted ? "text-blue-800" : ""}`}
    >
      {text}
    </span>
    <span className="text-sm text-gray-600">{caption}</span>
  </div>
);

/**
 * The same review twice, as it stands and with the remaining work tools
 * connected, on one card straight under the section's heading. The two are
 * rounded together, so the gap between them always shows the saving the
 * heading says. In the
 * summary it ends with the way into the breakdown, so each caption sits under
 * its number to leave it room. The detail passes no `onShowBreakdown`, and the
 * whole strip reads on one line.
 */
const SavingsTiles = ({
  person,
  onShowBreakdown,
}: {
  person: AveragePerson;
  onShowBreakdown?: () => void;
}) => {
  // Under the heading, so the gap reads as the heading's per-person saving.
  const pair = formatTimePair(person.today, person.today - person.saved, true);
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-md border bg-white px-5 py-4">
      <Figures
        text={pair.today}
        caption="per employee today"
        inline={!onShowBreakdown}
      />
      <TopicflowIcon type="arrowRight" className="h-5 w-5 text-gray-400" />
      <Figures
        text={pair.after}
        caption="with tools connected"
        highlighted
        inline={!onShowBreakdown}
      />
      {onShowBreakdown && (
        <button
          type="button"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
          onClick={onShowBreakdown}
        >
          <TopicflowIcon type="eye" className="h-4 w-4" />
          Show me the breakdown
        </button>
      )}
    </div>
  );
};

/** How many people's before-and-after to show: enough to see who gains most. */
const maxPeople = 3;

/**
 * One person's two times as numbers, and where they come from, on a card that
 * opens over their bar the moment the pointer is on it (or the bar has focus).
 * Plain CSS, with no delay: the row has no room for the numbers, so the card is
 * how they are read, and waiting for it would make the bar feel broken.
 */
const PersonCard = ({
  id,
  name,
  steps,
  today,
  after,
}: {
  id: string;
  name: string;
  steps: { step: ComplianceProgramStep; today: number; saved: number }[];
  today: number;
  after: number;
}) => {
  const label = useLabel();
  const names = stepNames(label);
  const pair = formatTimePair(today, after);
  const percent = today > 0 ? Math.round(((today - after) / today) * 100) : 0;
  const bar = (seconds: number, tone: string) => (
    <div className="h-1.5 flex-1 rounded-full bg-gray-100">
      <div
        className={`h-1.5 rounded-full ${tone}`}
        style={{ width: `${today > 0 ? (seconds / today) * 100 : 0}%` }}
      />
    </div>
  );
  return (
    <div
      id={id}
      role="tooltip"
      className="pointer-events-none invisible absolute bottom-full left-1/2 z-20 mb-3 w-80 -translate-x-1/2 rounded-lg border bg-white p-4 text-left text-sm shadow-lg group-hover:visible group-focus-within:visible"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate font-medium text-gray-900">{name}</span>
        <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
          saves {pair.saved} · {percent}%
        </span>
      </div>
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-3">
          <span className="w-20 shrink-0 text-gray-500">Today</span>
          {bar(today, "bg-gray-400")}
          <span className="w-24 shrink-0 whitespace-nowrap text-right font-medium text-gray-900">
            {pair.today}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="w-20 shrink-0 text-gray-500">With tools</span>
          {bar(after, "bg-blue-600")}
          <span className="w-24 shrink-0 whitespace-nowrap text-right font-medium text-blue-800">
            {pair.after}
          </span>
        </div>
      </div>
      {/* Where the time goes, step by step, so the saving has a reason. */}
      <ul className="mt-3 space-y-1 border-t pt-3 text-xs text-gray-600">
        {steps.map((line) => {
          const stepPair = formatTimePair(line.today, line.today - line.saved);
          return (
            <li key={line.step} className="flex justify-between gap-3">
              <span>{names[line.step]}</span>
              <span className="whitespace-nowrap tabular-nums">
                {stepPair.today}
                {line.saved > 0 && (
                  <span className="text-blue-700"> → {stepPair.after}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {/* A small arrow down to the bar. */}
      <span className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-b border-r bg-white" />
    </div>
  );
};

/**
 * The people who gain most, and how much each gets back. "Marcus gets 21
 * minutes back" lands harder than a company figure, and it shows that the
 * people who gain most are the ones writing self and manager reviews.
 */
const BeforeAndAfter = ({
  preview,
  seconds,
  savedFraction,
}: {
  preview: LoadPreview;
  seconds: number;
  savedFraction: (step: ComplianceProgramStep) => number;
}) => {
  const times = preview.writers.map((writer) => ({
    writer,
    ...writerTime(writer, preview, seconds, savedFraction),
  }));
  // Most time back first. A stable sort, so the backend's heaviest-first order
  // breaks the ties.
  const people = sortBy(times, (person) => -person.saved).slice(0, maxPeople);
  const longest = Math.max(...people.map((person) => person.today), 1);
  const barText = (today: number, after: number) => {
    const pair = formatTimePair(today, after);
    return `Today ${pair.today} · With tools connected ${pair.after}`;
  };

  return (
    <div>
      <ul className="space-y-2.5 tabular-nums">
        {people.map(({ writer, today, after }, index) => {
          const cardId = `person-card-${writer.user?.id ?? `hidden-${index}`}`;
          return (
            <li
              key={writer.user?.id ?? `hidden-${index}`}
              className="grid grid-cols-[10rem_1fr_7rem] items-center gap-3 text-sm"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Avatar user={writer.user} size="5" />
                <span className="truncate">
                  {writer.user?.name ?? "Someone"}
                </span>
              </span>
              {/* The bar shows the two times as lengths; the card over it says
                  them as numbers, for which the row has no room. A taller hit
                  area than the bar, so it is easy to land on. */}
              <div
                className="group relative -my-1.5 cursor-default py-1.5 outline-none"
                tabIndex={0}
                aria-label={barText(today, after)}
                aria-describedby={cardId}
              >
                <div className="relative h-2.5">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-gray-200 group-hover:bg-gray-300"
                    style={{ width: `${(today / longest) * 100}%` }}
                  />
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-blue-600 group-hover:bg-blue-700"
                    style={{ width: `${(after / longest) * 100}%` }}
                  />
                </div>
                <PersonCard
                  id={cardId}
                  name={writer.user?.name ?? "Someone"}
                  steps={writerSteps(writer, preview, seconds, savedFraction)}
                  today={today}
                  after={after}
                />
              </div>
              {/* The bar already shows before and after; the words say only
                  what changes. */}
              <span className="text-right font-medium text-blue-800">
                saves {formatTimePair(today, after).saved}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** What the two colours of the bars mean. */
const BarLegend = () => (
  <div className="flex flex-wrap gap-4 text-xs text-gray-600">
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm bg-gray-200" />
      Today
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm bg-blue-600" />
      With tools connected
    </span>
  </div>
);

/**
 * How much work the review on this form will ask of people, before it is
 * published: how long it takes each employee, split between managers and
 * everyone else, and how much of that connecting the rest of the work tools
 * could take off.
 *
 * Runs as soon as the tab is shown, with no button to open it: choosing this tab
 * is the admin asking the question.
 *
 * Nothing here saves. The query takes the form's current values as arguments and
 * hands back an answer; the review is written only when the admin saves the form.
 * The time per question is a guess the admin makes on the screen, so changing it
 * multiplies the answer already in hand instead of asking again.
 */
const ProgramFormReadiness = ({
  proposedComplianceProgram,
}: {
  proposedComplianceProgram: ProgramForm;
}) => {
  const { noun } = useProgramsSection();
  const [secondsPerQuestion, setSecondsPerQuestion] = useState(
    defaultSecondsPerQuestion,
  );

  const { preview, loading, error, refusal, waitingForASelection } =
    useProgramFormLoadPreview(proposedComplianceProgram);
  const hasFigures =
    !waitingForASelection &&
    preview !== undefined &&
    preview.writers.length > 0;

  const [savingsPercent, setSavingsPercent] = useState(
    defaultToolSavingsPercent,
  );
  // Only what is still to connect can save anything, and only in the steps its
  // evidence reaches.
  const { categories, loaded: toolsLoaded } = useReadinessTools(
    proposedComplianceProgram.organizationId,
  );
  const unconnected = categories.filter((category) => !category.connected);
  const savedFraction = (step: ComplianceProgramStep) =>
    stepSavedFraction(step, categories, savingsPercent);
  // Worked out once per change, not on every render: an org has thousands of
  // writers.
  const groups = useMemo(
    () =>
      preview
        ? writerGroups(preview, secondsPerQuestion, (step) =>
            stepSavedFraction(step, categories, savingsPercent),
          )
        : [],
    [preview, secondsPerQuestion, categories, savingsPercent],
  );
  const savedSeconds = groups.reduce(
    (sum, group) => sum + group.savedSeconds,
    0,
  );
  const person = averagePerson(groups);

  // The summary comes first: the one saving, and a way into the figures behind
  // it. It only makes sense while there is a saving to claim; otherwise the
  // breakdown is all there is to show. Once opened, the breakdown stays open.
  const [showBreakdown, setShowBreakdown] = useState(false);
  const showSummary =
    hasFigures && preview !== undefined && savedSeconds > 0 && !showBreakdown;
  // Which view to open on depends on what is connected, so neither is shown
  // until that is known: a saving claimed for a tool that turns out to be
  // connected would flash up and then vanish.
  const waitingForTools = hasFigures && !toolsLoaded;

  // The guesses every figure rests on, in boxes the admin can change.
  const minutesBox = () => (
    <AssumptionBox
      ariaLabel="Minutes per question"
      value={secondsPerQuestion}
      parse={parseMinutesPerQuestion}
      format={(value) => String(value / 60)}
      onChange={(value) => value !== null && setSecondsPerQuestion(value)}
      restoreOnBlur
      suffix="m"
    />
  );
  const savingBox = () => (
    <AssumptionBox
      ariaLabel="Saving from work tools"
      value={savingsPercent}
      parse={parsePercent}
      format={String}
      onChange={(value) => value !== null && setSavingsPercent(value)}
      restoreOnBlur
      suffix="%"
    />
  );

  // The sentence of guesses is the detail's only.
  const canSave = savedSeconds > 0;
  // While there is a saving, the section's heading claims it and the cards
  // need none. Otherwise the detail says how long the review takes.
  const heading = !canSave && (
    <div className="tf-header-base-dark">
      Your {noun} Could Take <Figure>{formatHours(person.today)}</Figure> per
      Employee to Write
    </div>
  );
  const assumptions = (
    // On a gray box at the foot of the card, as a note on the figures above.
    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-600">
      <span>Assuming</span>
      {minutesBox()}
      {canSave ? (
        <>
          <span>per question, with a</span>
          {savingBox()}
          <span>reduction in time from connecting more tools.</span>
        </>
      ) : (
        <span>per question.</span>
      )}
    </div>
  );

  // A failed request must never read as "nobody writes anything", and must
  // never leave old figures looking freshly checked. Both views show it.
  const errorNote = error && (
    <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-900">
      {refusal ||
        (preview
          ? "This could not be refreshed, so it may be out of date. Reload the page to try again."
          : "This could not be loaded. Reload the page to try again.")}
    </div>
  );
  // Said out loud, because a figure that leaves the peers out would otherwise
  // read as a review with no peer work in it. Both views show it.
  const peerNote = preview?.peerCountUnknown && (
    <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
      Peers are chosen after this {noun.toLowerCase()} starts, so their reviews
      are not counted here.
    </div>
  );

  return (
    <>
      <section>
        {/* The saving is the point of the section, so it heads it. With nothing
          left to save, or until what is connected is known, it would be a
          claim the cards under it cannot back. */}
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">
            {hasFigures && toolsLoaded && canSave ? (
              <>
                {/* Per person, which anyone can weigh, and across the whole
                    org, which a per-person figure hides. Both are rounded
                    from the same saving, not the per-person one from the
                    tiles' rounded pair, so one times the people is near the
                    other. An average, not a most: the people under it can
                    save more. */}
                Connecting Integrations Could Save an Average of{" "}
                <Figure>{formatPersonMinutes(person.saved)}/person</Figure>, and{" "}
                <Figure>{formatMinutes(savedSeconds)}</Figure> Across the Org
              </>
            ) : (
              `${noun} Readiness and Effort Analysis`
            )}
          </h2>
          {loading && <Loading size="4" mini />}
        </div>
        {/* One card per question the admin is asking, rather than one card
          holding them all. */}
        <div className="mt-3 space-y-4">
          {waitingForTools ? (
            <div className="rounded-md border bg-white p-6">
              <Loading size="5" mini />
            </div>
          ) : showSummary ? (
            // The tiles and a way into the figures behind them. The guesses the
            // figures rest on are left to the detail, where they can be changed
            // alongside the figures they move.
            <>
              {errorNote}
              {peerNote}
              <div>
                <SavingsTiles
                  person={person}
                  onShowBreakdown={() => setShowBreakdown(true)}
                />
              </div>
            </>
          ) : (
            // The detail: the tiles, then who writes how much in one card, with
            // the guesses the figures rest on at its foot. An even gap
            // between whatever the card holds. Who gains most has a card of its
            // own under it.
            <>
              {hasFigures && canSave && <SavingsTiles person={person} />}
              <div className="flex flex-col gap-4 rounded-md border bg-white p-6">
                {hasFigures && preview && heading}

                {waitingForASelection ? (
                  <div className="text-sm text-gray-500">
                    Choose who takes part on the Participants tab first.
                  </div>
                ) : (
                  <>
                    {errorNote}
                    {peerNote}
                    {preview ? (
                      hasFigures ? (
                        <>
                          <TimeTable groups={groups} canSave={canSave} />
                          {assumptions}
                        </>
                      ) : (
                        // Peers left uncounted are the reason, when they are; the
                        // note above says so, and this advice would be wrong.
                        !preview.peerCountUnknown && (
                          <div className="text-sm text-gray-500">
                            Nobody has questions to answer yet. Turn on a step
                            on the Workflow tab and choose its questions on the
                            Questions tab.
                          </div>
                        )
                      )
                    ) : (
                      !error && (
                        <div>
                          <Loading size="5" mini />
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
              {hasFigures && preview && canSave && (
                <div className="rounded-md border bg-white p-6">
                  {/* The legend in the top right, beside the heading, so the
                    rows end the card. */}
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    <div className="tf-header-base-dark">
                      Your Most Impacted Employees
                    </div>
                    <BarLegend />
                  </div>
                  <div className="mt-4">
                    <BeforeAndAfter
                      preview={preview}
                      seconds={secondsPerQuestion}
                      savedFraction={savedFraction}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      {/* The tools that would do it, as a section of its own with a card per
        category, Connect on each one missing. Shown in both views. Once every
        one is connected, it only says what people draw on. Not until the
        connected types are known, so a tool connected through an integration
        row never flashes up as missing. */}
      {toolsLoaded && (
        <section>
          <h2 className="text-lg font-semibold">
            {unconnected.length > 0
              ? "Recommended Integrations"
              : "What People Will Have to Draw On"}
          </h2>
          <div className="mt-3">
            {/* The summary lists only the categories and where to connect
              them; the detail says what each one adds. */}
            <ReadinessIntegrations
              organizationId={proposedComplianceProgram.organizationId}
              compact={showSummary}
            />
          </div>
        </section>
      )}
    </>
  );
};

export default ProgramFormReadiness;
